import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CRMCalendar() {
  const { token } = useCRMAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTasks(); }, [currentDate]);

  const fetchTasks = async () => {
    setLoading(true);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const start = new Date(year, month, 1).toISOString().split('T')[0];
    const end = new Date(year, month + 1, 0).toISOString().split('T')[0];
    
    try {
      const res = await fetch(`${API}/api/crm/tasks/?start_date=${start}&end_date=${end}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTasks((await res.json()).tasks || []);
    } catch (err) {}
    finally { setLoading(false); }
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  };

  const getTasksForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(t => t.due_date?.startsWith(dateStr));
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const today = () => setCurrentDate(new Date());

  const isToday = (day) => {
    const now = new Date();
    return day === now.getDate() && currentDate.getMonth() === now.getMonth() && currentDate.getFullYear() === now.getFullYear();
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-amber-500', in_progress: 'bg-blue-500', submitted: 'bg-purple-500', completed: 'bg-emerald-500' };
    return colors[status] || 'bg-slate-500';
  };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Calendar</h1>
          <p className="text-slate-500">View tasks and deadlines</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={today}>Today</Button>
          <Button variant="outline" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="text-center mb-4">
          <h2 className="text-xl font-semibold text-slate-900">
            {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth().map((day, idx) => (
                <div key={idx} className={`min-h-[100px] p-1 border rounded-lg ${day ? 'bg-white' : 'bg-slate-50'} ${isToday(day) ? 'ring-2 ring-indigo-500' : ''}`}>
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-indigo-600' : 'text-slate-700'}`}>{day}</div>
                      <div className="space-y-1">
                        {getTasksForDay(day).slice(0, 3).map(task => (
                          <div key={task.id} className={`text-xs p-1 rounded truncate text-white ${getStatusColor(task.status)}`} title={task.title}>
                            {task.title}
                          </div>
                        ))}
                        {getTasksForDay(day).length > 3 && (
                          <div className="text-xs text-slate-500">+{getTasksForDay(day).length - 3} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      <div className="flex items-center gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-amber-500" /><span>Pending</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-blue-500" /><span>In Progress</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-purple-500" /><span>Submitted</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-emerald-500" /><span>Completed</span></div>
      </div>
    </CRMLayout>
  );
}
